/* Shared cart-page and drawer delivery progress, driven by confirmed Shopify totals. */
(() => {
  if (customElements.get('candy-shipping-progress')) return;
  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.wasComplete = this.classList.contains('c4u-shipping--complete');
      this.onCart = event => this.update(event.detail);
      document.addEventListener('candy:cart:updated', this.onCart);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.cartData && Number.isFinite(event.cartData.total_price)) this.update(event.cartData);
          else this.refresh();
        });
      }
    }
    disconnectedCallback() {
      document.removeEventListener('candy:cart:updated', this.onCart);
      if (this.unsubscribe) this.unsubscribe();
      if (this.controller) this.controller.abort();
      this.querySelector('.c4u-shipping__confetti')?.remove();
    }
    refresh() {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {signal:this.controller.signal})
        .then(response => { if (!response.ok) throw Error('Cart unavailable'); return response.json(); })
        .then(cart => { if (this.isConnected) this.update(cart); }).catch(() => {});
    }
    celebrate() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rect = this.getBoundingClientRect();
      if (!rect.height || rect.right < 0 || rect.left >= innerWidth || rect.bottom < 0 || rect.top >= innerHeight) return;
      this.querySelector('.c4u-shipping__confetti')?.remove();
      const layer = document.createElement('span');
      layer.className = 'c4u-shipping__confetti';
      layer.setAttribute('aria-hidden', 'true');
      this.append(layer);
      const colours = ['#f4e9f4', '#d61da4', '#75009b', '#efb4e5'];
      const animations = [];
      for (let i = 0; i < 24; i++) {
        const piece = document.createElement('i');
        piece.style.setProperty('--confetti-color', colours[i % colours.length]);
        layer.append(piece);
        const x = (i / 23 - .5) * rect.width / innerWidth * 95;
        const rise = rect.height / innerWidth * (35 + (i % 5) * 8);
        animations.push(piece.animate([
          {transform:'translate(0vw,0vw) rotate(0deg)',opacity:0},
          {transform:`translate(${x * .6}vw,${-rise}vw) rotate(${i * 31}deg)`,opacity:1,offset:.4},
          {transform:`translate(${x}vw,${rect.height / innerWidth * 30}vw) rotate(${i * 67}deg)`,opacity:0}
        ], {duration:1000 + (i % 4) * 90,delay:(i % 3) * 35,easing:'cubic-bezier(.2,.65,.4,1)'}).finished.catch(() => {}));
      }
      Promise.all(animations).then(() => layer.remove());
    }
    update(cart) {
      if (!cart || !Number.isFinite(cart.total_price)) return;
      if (!Array.isArray(cart.items)) return this.refresh();
      const goal = Number(this.dataset.goal);
      if (!(goal > 0)) return;
      // Gift cards are emailed: they don't count towards free delivery, and a basket of only gift cards has no delivery.
      const cards = cart.items.filter(item => item.gift_card);
      this.style.display = cart.items.length && cards.length === cart.items.length ? 'none' : '';
      const total = Math.max(0, cart.total_price - cards.reduce((sum, item) => sum + (item.final_line_price || 0), 0));
      const remaining = Math.max(0, goal - total);
      const progress = Math.min(100, total / goal * 100);
      const message = this.querySelector('.c4u-shipping__message');
      const track = this.querySelector('[role="progressbar"]');
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:cart.currency || this.dataset.currency || 'GBP'}).format(remaining / 100);
      const complete = remaining === 0;
      this.classList.toggle('c4u-shipping--complete', complete);
      if (complete && !this.wasComplete) this.celebrate();
      this.wasComplete = complete;
      if (!complete) this.querySelector('.c4u-shipping__confetti')?.remove();
      const eyebrow = this.querySelector('.c4u-shipping__eyebrow');
      if (eyebrow) {
        const goalMoney = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:cart.currency || this.dataset.currency || 'GBP',minimumFractionDigits:0,maximumFractionDigits:2}).format(goal / 100);
        eyebrow.textContent = complete ? 'The delivery is on us'
          : total === 0 ? `Free delivery starts at ${goalMoney}`
          : progress < 1 ? "You're less than 1% of the way there"
          : `You're ${Math.floor(progress)}% of the way there`;
      }
      const copy = remaining ? `You're ${money} away from free delivery.` : "You've got free delivery!";
      if (message.textContent !== copy) message.textContent = copy;
      track.setAttribute('aria-valuenow', String(Math.round(progress)));
      this.querySelector('.c4u-shipping__fill').style.width = progress + '%';
    }
  }
  customElements.define('candy-shipping-progress', ShippingProgress);
})();
